import "./UnsupervisedResults.css";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Plot from "react-plotly.js";
import Papa from "papaparse";
import { apiRequest, logout, getTaskStatus } from "../utils/api";

function UnsupervisedResults() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState("");
  const [visiblePlots, setVisiblePlots] = useState({});
  const progressRef = useRef(null);
  const navigate = useNavigate();

  const togglePlot = (algorithmIndex, plotType) => {
    const key = `${algorithmIndex}-${plotType}`;
    setVisiblePlots(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => {
    const selectedAlgorithms = localStorage.getItem("selectedAlgorithms");
    if (!selectedAlgorithms || selectedAlgorithms.length === 0) {
      navigate("/unsupervised-algorithm-selection");
      return;
    }
    fetchResults(selectedAlgorithms);

    const interval = setInterval(() => {
      const currentTime = new Date();
      const hours = currentTime.getHours() % 12 || 12;
      const minutes = currentTime.getMinutes().toString().padStart(2, "0");
      const seconds = currentTime.getSeconds().toString().padStart(2, "0");
      const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
      setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
    }, 1000);

    return () => {
      clearInterval(progressRef.current);
      clearInterval(interval);
    };
    // pollRef is in progressRef (reused for polling)
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const fetchResults = async (algorithms) => {
    setLoading(true);
    setProgress(0);

    // Fake progress animation while waiting
    const fakeProgress = setInterval(() => {
      setProgress((prev) => (prev >= 85 ? 85 : prev + 5));
    }, 800);

    try {
      const algorithmsArray = JSON.parse(algorithms || "[]");

      // Dispatch job — immediate 202 response
      const dispatch = await apiRequest("/api/algorithms/unsupervised/run", {
        method: "POST",
        body: { selected_algorithms: algorithmsArray },
      });
      const { task_id: taskId } = dispatch;

      // Poll for completion
      progressRef.current = setInterval(async () => {
        try {
          const statusRes = await getTaskStatus(taskId);
          if (statusRes.status === "completed") {
            clearInterval(progressRef.current);
            clearInterval(fakeProgress);
            setProgress(100);

            const data = statusRes.result || {};
            let resArray = [];
            if (data.status === "success" && Array.isArray(data.results)) {
              resArray = data.results;
            } else {
              setError(data.message || "Unexpected response structure.");
            }
            setResults(resArray);
            setError(null);
            setLoading(false);
          } else if (statusRes.status === "failed") {
            clearInterval(progressRef.current);
            clearInterval(fakeProgress);
            setError(statusRes.error_message || "Clustering job failed.");
            setLoading(false);
          }
        } catch (pollErr) {
          console.error("Polling error:", pollErr);
        }
      }, 4000);

    } catch (err) {
      clearInterval(fakeProgress);
      console.error("Error dispatching clustering job:", err);
      setError("Error starting clustering job.");
      setResults([]);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="unsupervised-results-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading clustering results... {progress}%</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="unsupervised-results-page">
        <div className="error-container">
          <h2>Error Loading Results</h2>
          <p>{error}</p>
          <button onClick={() => navigate("/unsupervised-algorithm-selection")} className="primary-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const filteredResults = results.filter(
    (result) =>
      result.algorithm &&
      result.algorithm !== "PCA (Principal Component Analysis)"
  );

  if (filteredResults.length === 0) {
    return (
      <div className="unsupervised-results-page">
        <div className="error-container">
          <h2>No Results Available</h2>
          <p>No clustering results were generated. Please try again.</p>
          <button onClick={() => navigate("/unsupervised-algorithm-selection")} className="primary-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="unsupervised-results-page">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-content">
          <div className="nav-left-group">
            <div className="nav-logo" onClick={() => navigate("/")}>
              <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
            </div>
            <button className="back-nav-button" onClick={() => navigate("/unsupervised-algorithm-selection")}>
              Back
            </button>
          </div>
          <div className="nav-right">
            <div className="digital-clock">{time}</div>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Clustering Results</h1>
          <p className="header-subtitle">
            Discover patterns and groups in your healthcare data
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        {/* Info Box */}
        <div className="info-box">
          <h3>Understanding Clustering Metrics</h3>
          <p>
            <strong>Silhouette Score:</strong> Measures how similar objects are to their own cluster (range: -1 to 1, higher is better).
            <br />
            <strong>Calinski-Harabasz Score:</strong> Ratio of between-cluster to within-cluster variance (higher is better).
            <br />
            <strong>Davies-Bouldin Score:</strong> Average similarity ratio of clusters (lower is better).
          </p>
        </div>

        {/* Results Cards */}
        <div className="results-container">
          {filteredResults.map((result, index) => (
            <div className="result-card" key={index}>
              <div className="result-card-header">
                <h2>{result.algorithm}</h2>
              </div>

              <div className="result-card-content">
                {/* Metrics Table */}
                <div className="metrics-section">
                  <h3>Performance Metrics</h3>
                  <div className="table-wrapper">
    <table className="metrics-table">
      <thead>
        <tr>
          <th>Metric</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        {result.optimal_clusters && (
          <tr>
            <td>Optimal Clusters</td>
            <td>{result.optimal_clusters}</td>
          </tr>
        )}
                        {result.silhouette_score !== undefined && result.silhouette_score !== null && (
          <tr>
            <td>Silhouette Score</td>
            <td>{result.silhouette_score.toFixed(4)}</td>
          </tr>
        )}
                        {result.calinski_harabasz_score !== undefined && result.calinski_harabasz_score !== null && (
          <tr>
            <td>Calinski-Harabasz Score</td>
            <td>{result.calinski_harabasz_score.toFixed(2)}</td>
          </tr>
        )}
                        {result.davies_bouldin_score !== undefined && result.davies_bouldin_score !== null && (
          <tr>
            <td>Davies-Bouldin Score</td>
            <td>{result.davies_bouldin_score.toFixed(2)}</td>
          </tr>
        )}
      </tbody>
    </table>
        </div>
                </div>

                {/* Silhouette Description */}
                  {result.algorithm === "K-Means Clustering" &&
                    result.silhouette_score_description && (
                    <div className="description-box">
                      <h3>Silhouette Score Interpretation</h3>
                        <p>{result.silhouette_score_description}</p>
                      </div>
                    )}

                {/* Visualizations */}
                <div className="visualizations-section">
                  <div className="visualizations-header">
                    <h3>Visualizations</h3>
                    <div className="plot-buttons">
                      {result.plots?.dendrogram && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-dendrogram`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'dendrogram')}
                        >
                          Dendrogram
                        </button>
                      )}
                      {result.plots?.elbow_plot && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-elbow_plot`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'elbow_plot')}
                        >
                          Elbow Plot
                        </button>
                      )}
                      {result.plots?.scatter_dashboard && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-scatter_dashboard`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'scatter_dashboard')}
                        >
                          2D Scatter Plot
                        </button>
                      )}
                      {result.plots?.grouped_bar_chart && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-grouped_bar_chart`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'grouped_bar_chart')}
                        >
                          Grouped Analysis
                        </button>
                      )}
                      {result.plots?.threeD_dashboard && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-threeD_dashboard`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'threeD_dashboard')}
                        >
                          3D Visualization
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="plots-grid">
                    {result.plots?.dendrogram && visiblePlots[`${index}-dendrogram`] && (
                      <div className="plot-container full-width">
                        <h4>Dendrogram</h4>
                        <Plot
                          data={result.plots.dendrogram.data}
                          layout={{
                            ...result.plots.dendrogram.layout,
                            autosize: true,
                            margin: { l: 50, r: 50, t: 50, b: 100 }
                          }}
                          config={{ responsive: true, displayModeBar: true }}
                          style={{ width: '100%', height: '500px' }}
                        />
                      </div>
                    )}

                    {result.plots?.elbow_plot && visiblePlots[`${index}-elbow_plot`] && (
                      <div className="plot-container half-width">
                        <h4>Elbow Plot</h4>
                        <Plot
                          data={result.plots.elbow_plot.data}
                          layout={{
                            ...result.plots.elbow_plot.layout,
                            autosize: true,
                            margin: { l: 50, r: 50, t: 50, b: 50 }
                          }}
                          config={{ responsive: true, displayModeBar: true }}
                          style={{ width: '100%', height: '400px' }}
                        />
                      </div>
                    )}

                    {result.plots?.scatter_dashboard && visiblePlots[`${index}-scatter_dashboard`] && (
                      <div className="plot-container half-width">
                        <h4>2D Scatter Plot</h4>
                        <Plot
                          data={result.plots.scatter_dashboard.data}
                          layout={{
                            ...result.plots.scatter_dashboard.layout,
                            autosize: true,
                            margin: { l: 50, r: 50, t: 50, b: 50 }
                          }}
                          config={{ responsive: true, displayModeBar: true }}
                          style={{ width: '100%', height: '400px' }}
                        />
                      </div>
                    )}

                    {result.plots?.grouped_bar_chart && visiblePlots[`${index}-grouped_bar_chart`] && (
                      <div className="plot-container half-width">
                        <h4>Grouped Analysis</h4>
                        <Plot
                          data={result.plots.grouped_bar_chart.data}
                          layout={{
                            ...result.plots.grouped_bar_chart.layout,
                            autosize: true,
                            margin: { l: 50, r: 50, t: 50, b: 50 }
                          }}
                          config={{ responsive: true, displayModeBar: true }}
                          style={{ width: '100%', height: '400px' }}
                        />
                      </div>
                    )}

                    {result.plots?.threeD_dashboard && visiblePlots[`${index}-threeD_dashboard`] && (
                      <div className="plot-container full-width">
                        <h4>3D Visualization</h4>
                        <Plot
                          data={result.plots.threeD_dashboard.data}
                          layout={{
                            ...result.plots.threeD_dashboard.layout,
                            autosize: true,
                            margin: { l: 50, r: 50, t: 50, b: 50 }
                          }}
                          config={{ responsive: true, displayModeBar: true }}
                          style={{ width: '100%', height: '600px' }}
                        />
                      </div>
                    )}

                    {(() => {
                      const hasVisiblePlots = Object.keys(visiblePlots).some(key => 
                        key.startsWith(`${index}-`) && visiblePlots[key]
                      );
                      const hasAnyPlots = result.plots && (
                        result.plots.dendrogram || 
                        result.plots.elbow_plot || 
                        result.plots.scatter_dashboard || 
                        result.plots.grouped_bar_chart || 
                        result.plots.threeD_dashboard
                      );
                      return hasAnyPlots && !hasVisiblePlots;
                    })() && (
                      <div className="no-plots-message">
                        <p>Click on a visualization button above to view the plots</p>
                      </div>
                    )}
                  </div>
                  </div>

                {/* Download Button */}
                {result.clustered_data && (
                  <div className="download-section">
                    <button
                      onClick={() => {
                        const csv = Papa.unparse(result.clustered_data);
                        const blob = new Blob([csv], { type: "text/csv" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `clustered_data_${result.algorithm.replace(/\s+/g, "_")}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="download-button"
                      >
                        Download Clustered Dataset
                    </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>

        {/* Action Buttons */}
        <div className="action-section">
          <button onClick={() => navigate("/dashboard")} className="primary-button">
            End Analysis
          </button>
        </div>
      </div>
    </div>
  );
}

export default UnsupervisedResults;
